'use client';

import { Transition, TransitionChild } from '@headlessui/react';
import { usePathname } from 'next/navigation';
import { type FC, Fragment, useEffect } from 'react';
import { SocialLinks } from '@/components/SocialLinks';
import { cn } from '@/utils/cn';
import { getMenuTopOffset } from '@/utils/headerOffset';
import {
  isMenuItemGroup,
  isNavItemOrGroupActive,
} from '@/utils/navigation.utils';
import { MenuItem } from './Mobile/MenuItem';
import { MenuItemGroup } from './Mobile/MenuItemGroup';
import { NAVIGATION_DATA } from './navigationItems';

interface MobileNavigationProps {
  isMenuOpen: boolean;
  toggleMenu: () => void;
  className?: string;
}

export const MobileNavigation: FC<MobileNavigationProps> = ({
  isMenuOpen,
  toggleMenu,
  className,
}) => {
  const pathname = usePathname();

  // biome-ignore lint/correctness/useExhaustiveDependencies: we want to close the menu when the pathname changes
  useEffect(() => {
    if (isMenuOpen) {
      toggleMenu();
    }
  }, [pathname]);

  const offset = getMenuTopOffset();

  return (
    <Transition appear as={Fragment} show={isMenuOpen}>
      <TransitionChild
        as={Fragment}
        enter="ease-out duration-300"
        enterFrom="opacity-0 translate-y-[5%]"
        enterTo="opacity-100 translate-x-0"
        leave="ease-in duration-200"
        leaveFrom="opacity-100 translate-x-0"
        leaveTo="opacity-0 translate-y-[5%]"
      >
        <div
          className={cn(
            'fixed inset-y-0 left-0 z-20 w-full overflow-y-auto bg-mobile-menu pb-9 text-white xl:hidden',
            className
          )}
          style={{
            top: `${offset}px`,
          }}
        >
          <div className="flex h-full flex-col justify-between gap-8 px-4">
            <div className="mt-5 space-y-2">
              {NAVIGATION_DATA.map((item) => {
                if (isMenuItemGroup(item)) {
                  return (
                    <MenuItemGroup
                      key={item.id}
                      menuItemGroup={item}
                      pathname={pathname}
                    />
                  );
                }

                const isActive = isNavItemOrGroupActive({
                  currentPath: pathname,
                  item,
                });

                return (
                  <MenuItem
                    currentPath={pathname}
                    isActive={isActive}
                    item={item}
                    key={item.id}
                  />
                );
              })}
            </div>
            <div className="pl-3">
              <SocialLinks />
            </div>
          </div>
        </div>
      </TransitionChild>
    </Transition>
  );
};
